import type { Employer, EmployerProDoNotContact, UserProfile } from '@factoryfixinc/ats-interfaces';

import { useFFRestApi } from '@/composables/useApi';
import { InternalError } from '@/core/shared/errors/internal.error';
import { SanitizedRequestError } from '@/core/shared/errors/sanitized-request.error';

import type {
  DoNotContactEpisode,
  DoNotContactHistoryActor,
  DoNotContactSummary,
} from './do-not-contact.type';

export class DoNotContactPersistence {
  public async getSummary(
    employerId: number,
    userProfileId: number,
  ): Promise<DoNotContactSummary | null> {
    const filter = encodeURIComponent(JSON.stringify({ fields: ['id'] }));
    const { data, error, statusCode } = await useFFRestApi(
      `/employer/${employerId}/pro/${userProfileId}?filter=${filter}`,
    )
      .get()
      .json<{ id: number; doNotContact: DoNotContactSummary | null }>();
    if (!error.value && data.value?.id === userProfileId) {
      const summary = data.value.doNotContact;
      if (summary === null || isSummary(summary)) return summary;
    }
    throw new SanitizedRequestError('Failed to load Do Not Contact details', {
      status: statusCode.value,
      error: error.value,
      data: { employerId, userProfileId },
    });
  }

  public async getHistory(
    employerId: number,
    userProfileId: number,
  ): Promise<DoNotContactEpisode[]> {
    const { data, error, statusCode } = await useFFRestApi(
      `/employer/${employerId}/pro/${userProfileId}/do-not-contact`,
    )
      .get()
      .json<unknown>();
    if (
      !error.value &&
      Array.isArray(data.value) &&
      data.value.every(
        (row) =>
          isEpisode(row) && row.employerId === employerId && row.userProfileId === userProfileId,
      )
    ) {
      return data.value;
    }
    throw new SanitizedRequestError('Failed to load Do Not Contact history', {
      status: statusCode.value,
      error: error.value,
      data: { employerId, userProfileId },
    });
  }

  /**
   * Idempotent: an already marked pair returns its pre-existing episode. The
   * backend also rejects the candidate's active applications at the employer.
   */
  public async mark(
    employerId: Employer['id'],
    userProfileId: UserProfile['id'],
    reason?: string,
  ): Promise<EmployerProDoNotContact> {
    const url = `/employer/${employerId}/pro/${userProfileId}/do-not-contact`;
    const { data, error, statusCode } = await useFFRestApi(url)
      .post(reason ? { reason } : {})
      .json<EmployerProDoNotContact>();

    if (error.value || !data.value) {
      throw new InternalError('Failed to mark Do Not Contact', {
        error: error.value,
        status: statusCode.value,
        data: { employerId, userProfileId },
      });
    }

    return data.value;
  }

  public async unmark(employerId: Employer['id'], userProfileId: UserProfile['id']): Promise<void> {
    const url = `/employer/${employerId}/pro/${userProfileId}/do-not-contact`;
    const { error, statusCode } = await useFFRestApi(url).delete();

    if (error.value) {
      throw new InternalError('Failed to remove Do Not Contact', {
        error: error.value,
        status: statusCode.value,
        data: { employerId, userProfileId },
      });
    }
  }
}

function isSqlId(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= 4_294_967_295
  );
}

function isTimestamp(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function isActor(value: unknown): value is DoNotContactHistoryActor | null | undefined {
  if (value == null) return true;
  if (typeof value !== 'object') return false;
  const actor = value as DoNotContactHistoryActor;
  return (
    isSqlId(actor.id) &&
    [actor.nameFirst, actor.nameLast, actor.avatarUrl].every(
      (field) => field == null || typeof field === 'string',
    )
  );
}

function isEpisode(value: unknown): value is DoNotContactEpisode {
  if (!value || typeof value !== 'object') return false;
  const row = value as DoNotContactEpisode;
  return (
    isSqlId(row.id) &&
    isSqlId(row.employerId) &&
    isSqlId(row.userProfileId) &&
    isSqlId(row.createdByUserProfileId) &&
    isTimestamp(row.createTs) &&
    (row.removedTs == null || isTimestamp(row.removedTs)) &&
    (row.removedByUserProfileId == null || isSqlId(row.removedByUserProfileId)) &&
    isActor(row.createdBy) &&
    isActor(row.removedBy)
  );
}

function isSummary(value: unknown): value is DoNotContactSummary {
  if (!value || typeof value !== 'object') return false;
  const summary = value as DoNotContactSummary;
  return (
    summary.active === true &&
    isTimestamp(summary.createTs) &&
    (summary.reason === null || typeof summary.reason === 'string') &&
    summary.createdBy !== undefined &&
    isActor(summary.createdBy)
  );
}
